import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CampaignIcon from "@mui/icons-material/Campaign";
import MarketingChannelTable from "./MarketingChannelTable";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";

const MarketingChannel = () => {
  const [open, setOpen] = useState(false);
  const [channelName, setChannelName] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCreate = async () => {
    if (!channelName.trim()) return;
    setLoading(true);
    try {
      await axiosConfig({
        method: "post",
        url: "/advertisement/marketing-channel/",
        data: { name: channelName.trim() },
      });
      toast.success("Channel created successfully!");
      setRefreshKey((k) => k + 1);
      setChannelName("");
      setOpen(false);
    } catch (e) {
      const errMsg =
        e.response?.data?.name?.[0] ||
        e.response?.data?.detail ||
        e.response?.data?.message ||
        e.message ||
        "Failed to create channel";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setChannelName("");
    setOpen(false);
  };

  return (
    <Box sx={{ background: "#f1f5f9", minHeight: "100vh" }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "#fff",
          borderBottom: "1px solid #e2e8f0",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              background: "#04343a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CampaignIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 800,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              Marketing Channel
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
              Manage your marketing channels
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setOpen(true)}
          sx={{
            height: 36,
            borderRadius: "8px",
            background: "#016551",
            textTransform: "none",
            fontSize: 13,
            fontWeight: 600,
            px: 2,
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": {
              background: "linear-gradient(135deg,#1db954,#17a349)",
            },
          }}
        >
          Create Channel
        </Button>
      </Box>

      <Box sx={{ p: 3 }}>
        <MarketingChannelTable refreshKey={refreshKey} />
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 400 } }}
      >
        <DialogTitle
          sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", pb: 1 }}
        >
          Create Channel
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Channel Name"
            placeholder="Enter channel name"
            value={channelName}
            onChange={(e) => setChannelName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handleClose}
            size="small"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              color: "#64748b",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleCreate}
            disabled={!channelName.trim() || loading}
            startIcon={
              loading ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : null
            }
            sx={{
              borderRadius: "8px",
              background: "#016551",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg,#1db954,#17a349)",
              },
            }}
          >
            {loading ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketingChannel;
